import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Paper, TextField, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { colors, uiFont } from '../theme';

const PRIMARY_GENRES = ['House', 'Techno', 'Deep House', 'Minimal', 'Electro', 'Breaks', 'Disco', 'Ambient'];
const PRIMARY_LIMIT = 8;
const selectionSx = selected => ({
  color: selected ? colors.accent : colors.secondary,
  backgroundColor: selected ? colors.tint : colors.surface,
  border: `1px solid ${selected ? colors.accent : colors.controlBorder}`,
  '&:hover': { backgroundColor: colors.tint, borderColor: colors.accent, color: colors.text },
});

function GenreFilter({ genres, counts, value, onChange, expanded, onExpandedChange }) {
  const [query, setQuery] = useState('');
  useEffect(() => { if (!expanded) setQuery(''); }, [expanded]);

  const primaryGenres = useMemo(() => {
    const options = genres.filter(genre => genre !== 'Todos');
    const preferred = PRIMARY_GENRES.flatMap(name => options.filter(genre => genre.toLowerCase() === name.toLowerCase()));
    const popular = options.filter(genre => !preferred.includes(genre) && genre.length <= 18)
      .sort((a, b) => (counts.get(b) || 0) - (counts.get(a) || 0) || a.localeCompare(b));
    return [...preferred, ...popular].slice(0, PRIMARY_LIMIT);
  }, [genres, counts]);

  // Keep a selection from the panel visible, without adding another chip row.
  const visibleGenres = value !== 'Todos' && !primaryGenres.includes(value)
    ? [value, ...primaryGenres.slice(0, PRIMARY_LIMIT - 1)]
    : primaryGenres;
  const matchingGenres = genres.filter(genre => genre !== 'Todos' && genre.toLowerCase().includes(query.trim().toLowerCase()));
  const hasMoreGenres = genres.some(genre => genre !== 'Todos' && !visibleGenres.includes(genre));
  const selectGenre = genre => {
    onChange(genre);
    onExpandedChange(false);
  };

  return (
    <Box component="section" aria-labelledby="genre-filter-title" sx={{ minWidth: 0 }}>
      <Typography id="genre-filter-title" component="h4" variant="caption" sx={{
        display: 'block', mb: 0.6, fontSize: '0.65rem', color: colors.secondary, textTransform: 'uppercase', letterSpacing: 0.6,
      }}>Generos</Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
        <Box role="group" aria-label="Géneros principales" sx={{
          display: 'flex', gap: 0.75, flex: 1, minWidth: 0, alignItems: 'center',
          flexWrap: { xs: 'nowrap', md: 'wrap' }, overflowX: { xs: 'auto', md: 'visible' },
          pb: { xs: 0.5, md: 0 }, '&::-webkit-scrollbar': { height: 3 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: colors.controlBorder },
        }}>
          {['Todos', ...visibleGenres].map(genre => (
            <Tooltip key={genre} title={genre} describeChild>
              <Chip component="button" type="button" label={genre} clickable title={genre}
                aria-pressed={value === genre} onClick={() => selectGenre(genre)} sx={{
                  ...selectionSx(value === genre), borderRadius: 1, flexShrink: 0,
                  height: { xs: 40, md: 36 }, maxWidth: { xs: 150, md: 180 },
                  fontSize: '0.66rem', fontWeight: 500,
                  '& .MuiChip-label': { px: 1.15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
                }} />
            </Tooltip>
          ))}
        </Box>
        {hasMoreGenres && <Button variant="text" onClick={() => onExpandedChange(!expanded)}
          aria-expanded={expanded} aria-controls="all-genres-panel"
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{
            flexShrink: 0, minHeight: { xs: 40, md: 36 }, px: 0.75,
            color: colors.secondary, fontSize: '0.65rem', whiteSpace: 'nowrap',
            '&:hover': { backgroundColor: colors.tint, color: colors.accent },
          }}>{expanded ? 'Menos generos' : 'Mas generos'}</Button>}
      </Box>
      {expanded && <Paper id="all-genres-panel" variant="outlined" sx={{
        mt: 1, p: { xs: 1, sm: 1.5 }, backgroundColor: colors.elevated, borderColor: colors.border,
      }}>
        <TextField fullWidth size="small" type="search" placeholder="Buscar genero..." value={query}
          inputProps={{ 'aria-label': 'Buscar género', maxLength: 80 }} onChange={event => setQuery(event.target.value)}
          sx={{ mb: 1, '& input': { fontSize: '0.8rem' } }} />
        <Box role="group" aria-label="Todos los géneros" sx={{
          display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
          gap: 0.75, maxHeight: 240, overflowY: 'auto', p: 0.5,
        }}>
          {matchingGenres.map(genre => (
            <Tooltip key={genre} title={genre} describeChild>
              <Button title={genre} aria-pressed={value === genre} onClick={() => selectGenre(genre)} sx={{
                ...selectionSx(value === genre), justifyContent: 'flex-start', minWidth: 0, minHeight: 40, px: 1,
                fontFamily: uiFont, fontSize: '0.66rem',
              }}><Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{genre}</Box></Button>
            </Tooltip>
          ))}
          {!matchingGenres.length && <Typography variant="body2" sx={{ gridColumn: '1 / -1', py: 1, color: colors.secondary }}>
            No encontramos géneros con ese nombre.
          </Typography>}
        </Box>
      </Paper>}
    </Box>
  );
}

export default GenreFilter;
